/**
 * The authored hills of Dig, in play order, each a little harder than the one before. A hill is a
 * small data definition: the shape of the sand (mounds added together, plus a ripple), where each
 * creature sits and how deep, and the odd rock. world.ts turns a definition into cells; the same
 * definition always makes the same hill.
 *
 * Once a hill has shipped, do not change it: documents replay "next hill" events by hill number
 * (events.ts), so editing a hill would change hills already in people's documents. Add new hills at
 * the end instead (and bump DIG_FOR_EYES_VERSION if an old one must change).
 */

/** A mound of sand: a rounded bump of `height` cells, elliptic footprint (radii in cells), turned by `angle` (radians). */
export type Mound = { x: number; z: number; rx: number; rz: number; height: number; angle?: number }

/**
 * A buried creature. (x, z) is the middle of its top; `cover` is how many cells of sand lie over its
 * eyes at the lowest point of the sand over the whole creature (so somewhere it is exactly that
 * deep, elsewhere deeper). `eyes`: 2 is a big pebble with two eyes, 1 a small pebble with one big
 * eye. `color` indexes DIG_CREATURE_COLORS.
 */
export type HillCreature = { x: number; z: number; cover: number; eyes: 1 | 2; color: number }

/**
 * A rock (solid, the spade never takes it) sitting in the sand at (x, z): a squat boulder of radius
 * `r` whose middle is `sink` cells below the sand surface there. Never touching a creature.
 */
export type Rock = { x: number; z: number; r: number; sink: number }

export type Hill = {
  mounds: Mound[]
  /** Ripple on the sand (cells), and the seed of its noise. */
  ripple: number
  noiseSeed: number
  creatures: HillCreature[]
  rocks?: Rock[]
}

export const HILLS: readonly Hill[] = [
  // 1. A small round hill, three big creatures just under the surface.
  {
    mounds: [{ x: 32, z: 32, rx: 24, rz: 22, height: 10 }],
    ripple: 0.8,
    noiseSeed: 11,
    creatures: [
      { x: 24, z: 28, cover: 2, eyes: 2, color: 0 },
      { x: 40, z: 29, cover: 2, eyes: 2, color: 1 },
      { x: 32, z: 40, cover: 2, eyes: 2, color: 3 },
    ],
  },
  // 2. A bigger hill, four creatures, some a cell deeper.
  {
    mounds: [{ x: 31, z: 33, rx: 27, rz: 25, height: 13 }],
    ripple: 1,
    noiseSeed: 22,
    creatures: [
      { x: 22, z: 25, cover: 2, eyes: 2, color: 2 },
      { x: 41, z: 26, cover: 3, eyes: 2, color: 4 },
      { x: 25, z: 41, cover: 3, eyes: 2, color: 5 },
      { x: 40, z: 40, cover: 2, eyes: 2, color: 0 },
    ],
  },
  // 3. Two hills with a dip between them; one creature hides in the dip, a small one appears.
  {
    mounds: [
      { x: 21, z: 25, rx: 18, rz: 17, height: 13 },
      { x: 43, z: 40, rx: 19, rz: 18, height: 15 },
    ],
    ripple: 1,
    noiseSeed: 33,
    creatures: [
      { x: 19, z: 23, cover: 3, eyes: 2, color: 1 },
      { x: 32, z: 33, cover: 2, eyes: 1, color: 6 },
      { x: 45, z: 42, cover: 4, eyes: 2, color: 3 },
      { x: 38, z: 47, cover: 3, eyes: 2, color: 2 },
    ],
  },
  // 4. A long ridge: five creatures strung along it, deeper in the middle.
  {
    mounds: [
      { x: 32, z: 32, rx: 30, rz: 28, height: 5 },
      { x: 32, z: 32, rx: 29, rz: 10, height: 12, angle: 0.55 },
    ],
    ripple: 1.1,
    noiseSeed: 44,
    creatures: [
      { x: 14, z: 23, cover: 3, eyes: 2, color: 0 },
      { x: 23, z: 27, cover: 4, eyes: 1, color: 4 },
      { x: 32, z: 32, cover: 5, eyes: 2, color: 5 },
      { x: 41, z: 37, cover: 4, eyes: 2, color: 1 },
      { x: 50, z: 41, cover: 3, eyes: 1, color: 6 },
    ],
  },
  // 5. Rocks: two creatures lie half under a boulder (one of them deep); dig in beside it and underneath.
  {
    mounds: [{ x: 32, z: 32, rx: 28, rz: 26, height: 15 }],
    ripple: 1.1,
    noiseSeed: 55,
    creatures: [
      { x: 23, z: 25, cover: 4, eyes: 2, color: 3 },
      { x: 42, z: 24, cover: 3, eyes: 2, color: 2 },
      { x: 33, z: 35, cover: 5, eyes: 2, color: 4 },
      { x: 21, z: 42, cover: 3, eyes: 1, color: 0 },
      { x: 43, z: 43, cover: 4, eyes: 2, color: 6 },
    ],
    rocks: [
      { x: 25, z: 24, r: 3, sink: 1 },
      { x: 35, z: 37, r: 3, sink: 1 },
    ],
  },
  // 6. The big hill: tall, six creatures, the deepest under six cells, small ones too, and a rock.
  {
    mounds: [
      { x: 32, z: 32, rx: 29, rz: 27, height: 16 },
      { x: 22, z: 42, rx: 12, rz: 12, height: 3 },
    ],
    ripple: 1.2,
    noiseSeed: 66,
    creatures: [
      { x: 20, z: 21, cover: 4, eyes: 2, color: 5 },
      { x: 34, z: 20, cover: 5, eyes: 1, color: 1 },
      { x: 45, z: 29, cover: 4, eyes: 2, color: 0 },
      { x: 30, z: 32, cover: 6, eyes: 2, color: 3 },
      { x: 21, z: 42, cover: 5, eyes: 1, color: 6 },
      { x: 39, z: 43, cover: 6, eyes: 2, color: 2 },
    ],
    rocks: [{ x: 32, z: 34, r: 3, sink: 0 }],
  },
]
